import { useQuery } from '@tanstack/react-query'
import { ArrowRight, BookOpen, CalendarRange, DoorOpen, GraduationCap, UsersRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ApiError } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { StatCard } from '@/components/ui/StatCard'
import { staffPortalQuery } from '@/features/portal/api'
import { SectionRoles } from '@/features/portal/SectionRoles'
import { SubjectChips } from '@/features/portal/SubjectChips'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate } from '@/utils/format'

export function StaffHomePage() {
  const { t } = useTranslation('portal')
  useDocumentTitle('My workspace')
  const { data, isPending, error, refetch } = useQuery(staffPortalQuery())

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <EmptyState
        icon={<GraduationCap />}
        title={t("Your account isn't linked yet")}
        description={error.message}
      />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const students = data.sections.reduce((n, s) => n + s.studentCount, 0)
  const classTeacherOf = data.sections.filter((s) => s.isClassTeacher).length

  return (
    <>
      <PageHeader
        title={t('Welcome, {{name}}', { name: data.profile.firstName })}
        description={[data.profile.designation?.name, data.profile.department?.name]
          .filter(Boolean)
          .join(' · ')}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label={t('My sections')}
          value={data.sections.length}
          icon={<GraduationCap />}
          hint={t('Class teacher of {{count}}', { count: classTeacherOf })}
        />
        <StatCard
          label={t('My students')}
          value={students}
          icon={<UsersRound />}
          tone="violet"
          hint={t('Across your sections')}
        />
        <StatCard
          label={t('Main subject')}
          value={<span className="text-lg">{data.profile.primarySubject?.name ?? '—'}</span>}
          icon={<BookOpen />}
          tone="info"
        />
      </div>

      {data.academicYear ? (
        <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-surface px-3 py-1.5 text-xs font-medium text-ink-soft ring-1 ring-line">
          <CalendarRange className="size-4 text-brand-600" aria-hidden="true" /> {data.academicYear.name} ·{' '}
          {formatDate(data.academicYear.startsOn)} – {formatDate(data.academicYear.endsOn)}
        </p>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {data.sections.length === 0 ? (
          <Card className="lg:col-span-2">
            <EmptyState
              icon={<GraduationCap />}
              title={t('No classes assigned yet')}
              description={t(
                'Your sections will appear here once the office assigns you as class teacher or in the timetable.',
              )}
            />
          </Card>
        ) : (
          data.sections.map((s) => (
            <Card key={s.id}>
              <CardHeader
                title={s.name}
                description={
                  [s.shift, s.room ? t('Room {{room}}', { room: s.room }) : null]
                    .filter(Boolean)
                    .join(' · ') || undefined
                }
                icon={<DoorOpen />}
                actions={
                  <Button asChild variant="ghost" size="sm" rightIcon={<ArrowRight />}>
                    <Link to={`/staff/classes?section=${s.id}`}>{t('Roster')}</Link>
                  </Button>
                }
              />
              <CardBody className="space-y-4">
                <SectionRoles section={s} />
                <div>
                  <div className="flex justify-between text-xs text-muted">
                    <span>{t('{{count}} students', { count: s.studentCount })}</span>
                    {s.capacity ? <span>{t('{{count}} seats', { count: s.capacity })}</span> : null}
                  </div>
                  {s.capacity ? (
                    <div className="mt-1.5 h-2 rounded-full bg-surface-sunken" aria-hidden="true">
                      <div
                        className="h-full rounded-full bg-brand-500"
                        style={{ width: `${Math.min(100, (s.studentCount / s.capacity) * 100)}%` }}
                      />
                    </div>
                  ) : null}
                </div>
                {s.isClassTeacher || s.classCover ? <SubjectChips subjects={s.subjects} /> : null}
              </CardBody>
            </Card>
          ))
        )}
      </div>
    </>
  )
}
